import { api } from 'api';
import KeyIcon from 'assets/icons/key-icon.svg';
import CloseIcon from 'assets/ver2/icons/close-blue-icon.svg';
import ButtonCommon from 'components/common/ButtonCommon';
import {
  DESKTOP_MIN_WIDTH,
  HHMT_BRAND,
  REMOX_BRAND,
  SCREEN_ID_PROFILE_SECURITY,
  SECURITY_PREFIX,
} from 'constants';
import { checkMaintenance } from 'helpers/maintenance';
import useAuth from 'hooks/useAuth';
import useAutoShowEventModal from 'hooks/useAutoShowEventModal';
import useLang from 'hooks/useLang';
import useQuery from 'hooks/useQuery';
import useStore from 'hooks/useStore';
import useTheme from 'hooks/useTheme';
import { useEffect, useState } from 'react';
import Navbar from 'react-bootstrap/Navbar';
import Offcanvas from 'react-bootstrap/Offcanvas';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { stringUtils } from 'utils';
import FormChangePasswordPopup from '../../../FormChangePasswordPopup';
import FormInputSwitch from '../../../FormInputSwitch';

const SCREEN_ID = SCREEN_ID_PROFILE_SECURITY;

const SecurityMobile = () => {
  const { t } = useLang();
  const { token } = useAuth();
  const { isHHMTTheme } = useTheme();
  const { state } = useStore();
  const { currentUser } = state;

  const [isEmailChecked, setEmailChecked] = useState(false);
  const [isSMSChecked, setSMSChecked] = useState(false);
  const [isShowFormResetPasswordPopup, setShowFormResetPasswordPopup] =
    useState(false);

  const navigate = useNavigate();
  const query = useQuery();
  const tab = query.get('tab');

  const handleOnClose = () => {
    navigate(SECURITY_PREFIX);
  };

  const fetchProfileOptins = async () => {
    try {
      const { data } = await api.getOptin(token);

      if (data) {
        const { optInByEmail, optInByText } = data;

        setEmailChecked(optInByEmail);

        setSMSChecked(optInByText);
      }
    } catch (error) {
      checkMaintenance(error);
      console.error(error?.message);
    }
  };

  useEffect(() => {
    if (currentUser) {
      fetchProfileOptins();
    }
  }, [currentUser]);

  const handleOptin = async (type, value, setValue) => {
    try {
      const { data } = await api.saveOptin(token, type, value);

      if (data) {
        setValue(value);
      }
    } catch (error) {
      checkMaintenance(error);
      console.error(error?.message);
    }
  };

  const handleOnToggleFormResetPasswordPopup = () => {
    setShowFormResetPasswordPopup(!isShowFormResetPasswordPopup);
  };

  useAutoShowEventModal({
    screenId: SCREEN_ID,
    params: [],
    modalStyles: {},
    modalOptions: {},
  });

  return (
    <>
      <style>
        {`
          .security-mobile-offcanvas {
            width: 100%!important;
            height: calc(100% - 70px);
            border: none!important;
          }
          .security-mobile-offcanvas-backdrop {
            opacity: 0!important;
          }
        `}
      </style>

      {isShowFormResetPasswordPopup && (
        <FormChangePasswordPopup
          isOpen={isShowFormResetPasswordPopup}
          onClose={handleOnToggleFormResetPasswordPopup}
        />
      )}

      <SecurityMobileStyled>
        <NavbarStyled expand={false} expanded={tab} onToggle={handleOnClose}>
          <NavbarOffcanvas
            id="offcanvasNavbar-expand-false"
            aria-labelledby="offcanvasNavbarLabel-expand-false"
            placement="end"
            className="security-mobile-offcanvas"
            backdropClassName="security-mobile-offcanvas-backdrop"
          >
            <OffcanvasHeader closeButton>
              <OffcanvasTitle id="offcanvasNavbarLabel-expand-false">
                {t('label_security')}
              </OffcanvasTitle>
              <img
                src={CloseIcon}
                width={20}
                height={20}
                onClick={handleOnClose}
              />
            </OffcanvasHeader>
            <OffcanvasBody>
              <Title>{t('form_password')}</Title>
              <ButtonCommon
                value={t('button_change_password')}
                color="var(--c-tertiary)"
                background="var(--ds-c-white)"
                isFill={false}
                styles={{
                  paddingInline: 0,
                }}
                borderNameVariable="--border-secondary"
                iconSrc={KeyIcon}
                iconStyles={{
                  filter: isHHMTTheme
                    ? 'var(--ds-f-c-yellow)'
                    : 'var(--ds-f-c-blue-dark)',
                }}
                onClick={handleOnToggleFormResetPasswordPopup}
              />
              <Divider />
              <Title>{t('label_communication_preferences')}</Title>
              <Desc>
                {stringUtils.replaceKeyword(
                  t('label_communication_preferences_desc'),
                  [
                    {
                      key: 'brand',
                      value: isHHMTTheme ? HHMT_BRAND : REMOX_BRAND,
                    },
                  ]
                )}
              </Desc>
              <Button
                onClick={() =>
                  handleOptin('EMAIL', !isEmailChecked, setEmailChecked)
                }
              >
                Email <FormInputSwitch isChecked={isEmailChecked} />
              </Button>
              <Button
                onClick={() => handleOptin('SMS', !isSMSChecked, setSMSChecked)}
              >
                SMS <FormInputSwitch isChecked={isSMSChecked} />
              </Button>
            </OffcanvasBody>
          </NavbarOffcanvas>
        </NavbarStyled>
      </SecurityMobileStyled>
    </>
  );
};

const SecurityMobileStyled = styled.div`
  position: relative;
`;

const NavbarStyled = styled(Navbar)`
  display: initial;
  position: absolute;
  top: -40px;
  right: 0;
  padding: 0;
  height: 40px;

  @media screen and (min-width: ${DESKTOP_MIN_WIDTH}px) {
    top: unset;
    right: unset;
  }
`;

const NavbarOffcanvas = styled(Navbar.Offcanvas)`
  margin-top: 70px;
`;
const OffcanvasHeader = styled(Offcanvas.Header)`
  min-height: 40px;
  padding: 0 16px;
  border-bottom: 1px solid #ececf0;

  display: flex;
  justify-content: space-between;

  & button {
    display: none;
  }

  & span {
    font-family: var(--ff-primary);
    font-style: normal;
    font-weight: 700;
    font-size: 16px;
    color: var(--ds-c-sky);
    cursor: pointer;
    user-select: none;

    &:hover {
      color: var(--ds-c-sky-hover);
    }
  }
`;
const OffcanvasTitle = styled(Offcanvas.Title)`
  font-family: var(--ff-primary);
  font-style: normal;
  font-weight: 500;
  font-size: 14px;
  line-height: 17px;
  text-transform: uppercase;
  color: #000;
`;
const OffcanvasBody = styled(Offcanvas.Body)`
  padding-inline: 16px;
  padding-block: 0px;
  padding-top: 16px;
  padding-bottom: 24px;
`;

const Title = styled.p`
  font-family: var(--ff-primary);
  font-style: normal;
  font-weight: 700;
  font-size: 16px;
  line-height: 20px;
  text-transform: uppercase;
  color: #0e1012;

  display: flex;
  align-items: center;
`;
const Desc = styled.p`
  font-family: var(--ff-primary);
  font-style: normal;
  font-weight: 500;
  font-size: 16px;
  line-height: 20px;
  color: #0e1012;

  display: flex;
  align-items: center;
`;

const Divider = styled.div`
  border-bottom: 1px solid #eef2f5;
  margin-bottom: 24px;
  margin-top: 24px;
`;

const Button = styled.div`
  font-family: var(--ff-primary);
  font-style: normal;
  font-weight: 500;
  font-size: 16px;
  line-height: 20px;
  color: #0e1012;

  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
  height: 24px;
`;

export default SecurityMobile;
